// src/pages/doctor/DocumentEditorModalV2Mobile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { applyRequiredBadges } from '../../utils/requiredBadge';
import ReactDOM from 'react-dom';
import AlertModal from '../common/AlertModal';

const jpGender = (g) => {
  const s = (g ?? '').toString();
  if (['male','0','男','男性','M','m'].includes(s))   return '男性';
  if (['female','1','女','女性','F','f'].includes(s)) return '女性';
  return 'その他';
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';
// iOS対応: YYYY/MM/DD → YYYY-MM-DD
const toDateInputValue = (val) => {
  if (!val) return '';
  return val.replace(/\//g, '-');
};
// 保存時: YYYY-MM-DD → YYYY/MM/DD
const fromDateInputValue = (val) => {
  if (!val) return '';
  return val.replace(/-/g, '/');
};

const DEFAULT_REFERRAL_PURPOSE_TEMPLATE = `平素よりお世話になっております。
上記患者様について、オンライン診療を実施したところ、対面での精査・加療が必要と判断したためご紹介いたします。
【受診理由】

【オンライン診療時所見】

【処方内容】

お手数をおかけいたしますが、ご高診のほどよろしくお願い申し上げます。`;

const DEFAULT_REFERRAL_PURPOSE_TEMPLATE_NOADDR = `平素よりお世話になっております。
上記患者様について、オンライン診療を実施したところ、対面での精査・加療が必要と判断したためご紹介いたします。
【受診理由】

【オンライン診療時所見】

【処方内容】`;

export default function DocumentEditorModalV2Mobile({
    open, onClose, onSave, selectedDoc, patient, primaryDiseaseDisplayName, initialFieldValues = {}
}) {
  const [vals, setVals] = useState({});
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const ensureArray = (v) => Array.isArray(v) ? v : (v == null || v === '' ? [] : [String(v)]);
  const docCode = (selectedDoc?.code || '').toString().toUpperCase();

  // ドキュメントごとの必須フィールド定義
  const requiredFields = useMemo(() => {
    switch (docCode) {
      case 'LIFESTYLE_DOCUMENT':
        // 氏名〜BMI + 現在血圧(最高/最低) + 目標血圧(収縮/拡張) を必須
        return [
          'full_name', 'birth_date', 'age', 'gender', 'primary_disease_name',
          'height_cm', 'weight_kg', 'bmi',
          'systolic_bp', 'diastolic_bp',
          'target_systolic_bp', 'target_diastolic_bp'
        ];
      case 'DOC_CERT':
        // 診断名・本文のみ必須
        return ['diagnosis_name', 'certificate_body_text'];
      case 'DOC_INFO_ADDR':
        // 宛先以外を必須
        return ['full_name', 'birth_date', 'age', 'gender', 'disease_name', 'referral_purpose_text'];
      case 'DOC_DISABILITY':
        // 傷病手当金申請書：傷病名は必須
        return ['disease_name'];
      case 'DOC_WORK_CERT':
        // 就労可能証明書：全項目必須
        return ['disease_name', 'current_condition', 'condition_at_retirement', 'work_possible_date', 'insurance_benefit_status'];
      case 'DOC_INFO_NOADDR':
      default:
        // 全項目必須（宛先項目が存在しない版）
        return ['full_name', 'birth_date', 'age', 'gender', 'disease_name', 'referral_purpose_text'];
    }
  }, [docCode]);


  // プリフィル初期化
  useEffect(() => {
    if (!open || !selectedDoc) return;
    const base = {
      name: patient?.name || '',
      birth_date: patient?.birth_date ? fmtDate(patient.birth_date) : '',
      age: patient?.age ?? '',
      gender: jpGender(patient?.gender || ''),
      address: '',
      disease_name: primaryDiseaseDisplayName || '',
      current_medications: patient?.current_medications || '',
      height_cm: patient?.height ?? '',
        weight_kg: patient?.weight ?? '',
        bmi: patient?.bmi ?? ''
    };

    let init = {};
    switch (selectedDoc.code) {
        case 'LIFESTYLE_DOCUMENT':
      // 療養計画書（保険・0円）
      init = {
        full_name: base.name,
        birth_date: base.birth_date,
        age: base.age,
        gender: base.gender,
        primary_disease_name: base.disease_name,
        height_cm: base.height_cm,
        weight_kg: base.weight_kg,
        bmi: base.bmi,
        // 現在値（空欄可・小数可）
        systolic_bp: '',     // 最高血圧 mmHg
        diastolic_bp: '',    // 最低血圧 mmHg
        // 目標値（空欄可・小数可）
        target_weight_kg: '',
        target_bmi: '',
       target_systolic_bp: '',
        target_diastolic_bp: '',
        target_hba1c: '',
        // 医師記入欄
        careplan_target_goal: '',
        careplan_target_action: '',
        food_guidance: [],
        food_free_text: '',
        athletic_guidance: [],
        athletic_free_text: '',
        tobacco_guidance: [],
        tobacco_free_text: '',
        other_guidance: [],
        other_free_text: ''
      };
     break;
      case 'DOC_CERT':
        init = {
          full_name: base.name,
          birth_date: base.birth_date,
          diagnosis_name: base.disease_name,
          certificate_body_text: ''
        };
        break;
      case 'DOC_INFO_ADDR':
        init = {
          full_name: base.name,
          birth_date: base.birth_date,
          age: base.age,
          gender: base.gender,
          disease_name: base.disease_name,
          addressee: patient?.family_doctor_clinic || '',
          referral_purpose_text: DEFAULT_REFERRAL_PURPOSE_TEMPLATE,
        };
        break;
      case 'DOC_DISABILITY':
        // 傷病手当金申請書
        init = {
          disease_name: base.disease_name,
          cause_of_illness: '不詳',
          onset_date: '',
          monthly_comparison: '変化なし',
          treatment_contents: [],
          symptoms: [],
          supplementary_notes: '上記症状持続のため労務は困難',
          work_inability_start_date: '',
          work_inability_end_date: ''
        };
        break;
      case 'DOC_WORK_CERT':
        // 就労可能証明書
        init = {
          disease_name: base.disease_name,
          current_condition: '',
          condition_at_retirement: '',
          work_possible_date: '',
          insurance_benefit_status: ''
        };
        break;
      case 'DOC_INFO_NOADDR':
      default:
        init = {
          full_name: base.name,
          birth_date: base.birth_date,
          age: base.age,
          gender: base.gender,
          disease_name: base.disease_name,
          referral_purpose_text: DEFAULT_REFERRAL_PURPOSE_TEMPLATE_NOADDR,
        };
        break;
    }
    const saved = initialFieldValues || {};
        setVals({
          ...init,
          ...saved,
          // ★ 保存済みから復元（型を厳格に配列へ）
          food_guidance:     ensureArray(saved.food_guidance),
          athletic_guidance: ensureArray(saved.athletic_guidance),
          tobacco_guidance:  ensureArray(saved.tobacco_guidance),
          other_guidance:    ensureArray(saved.other_guidance),
          // 傷病手当金申請書用
          treatment_contents: ensureArray(saved.treatment_contents),
          symptoms: ensureArray(saved.symptoms),
        });
  }, [open, selectedDoc?.id]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // ラベルに「※必須」バッジを自動付与
  useEffect(() => {
    if (!open) return;
    try {
      applyRequiredBadges('.medichat-docmodal-body_v2');
    } catch {}
  }, [open, docCode]);

  if (!open) return null;

  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const body = (() => {
    if (!selectedDoc) return null;
    switch (selectedDoc.code) {
        case 'LIFESTYLE_DOCUMENT':
      return (
        <>
          <SectionTitle title="基本情報" />
          <Field label="氏名" v={vals.full_name} onV={(v)=>set('full_name', v)} required={true} />
          <Field label="生年月日" v={vals.birth_date} onV={(v)=>set('birth_date', v)} required={true} />
          <Field label="年齢" v={vals.age} onV={(v)=>set('age', v)} required={true} />
          <Field label="性別" v={vals.gender} onV={(v)=>set('gender', v)} required={true} />
          <Field label="主病名" v={vals.primary_disease_name} onV={(v)=>set('primary_disease_name', v)} required={true} />

          <Group two>
            <NumField label="身長(cm)" v={vals.height_cm} onV={(v)=>set('height_cm', v)} required={true} />
            <NumField label="体重(kg)" v={vals.weight_kg} onV={(v)=>set('weight_kg', v)} required={true} />
          </Group>
          <NumField label="BMI" v={vals.bmi} onV={(v)=>set('bmi', v)} required={true} />

          <SectionTitle title="現在値" />
          <Group two>
            <NumField label="最高血圧(mmHg)" v={vals.systolic_bp} onV={(v)=>set('systolic_bp', v)} required={true} />
            <NumField label="最低血圧(mmHg)" v={vals.diastolic_bp} onV={(v)=>set('diastolic_bp', v)} required={true} />
          </Group>

          <SectionTitle title="目標値（空欄可）" />
          <Group two>
            <NumField label="目標体重(kg)" v={vals.target_weight_kg} onV={(v)=>set('target_weight_kg', v)} />
            <NumField label="目標BMI" v={vals.target_bmi} onV={(v)=>set('target_bmi', v)} />
          </Group>
          <Group two>
            <NumField label="目標収縮期血圧(mmHg)" v={vals.target_systolic_bp} onV={(v)=>set('target_systolic_bp', v)} required={true} />
            <NumField label="目標拡張期血圧(mmHg)" v={vals.target_diastolic_bp} onV={(v)=>set('target_diastolic_bp', v)} required={true} />
          </Group>
          <NumField label="目標HbA1c(%)" v={vals.target_hba1c} onV={(v)=>set('target_hba1c', v)} />

          <SectionTitle title="医師記入欄" />
          <TextArea label="達成目標（患者と相談した目標）" v={vals.careplan_target_goal} onV={(v)=>set('careplan_target_goal', v)} />
          <TextArea label="行動目標（患者と相談した目標）" v={vals.careplan_target_action} onV={(v)=>set('careplan_target_action', v)} />

          <SectionTitle title="重点を置く領域と指導項目" />
          <GuidanceGroup
            title="食事"
            name="food_guidance"
            values={vals.food_guidance}
            onChange={(arr)=>set('food_guidance', arr)}
            options={[
              '食事摂取量を適正にする',
              '食塩・調味料を控える',
              '野菜・きのこ・海藻など食物繊維の摂取を増やす',
              '油を使った料理（揚げ物や炒め物等）の摂取を減らす',
              '節酒',
              '間食減らす',
              '食事時間：朝食、昼食、夕食を規則正しくとる',
            ]}
            freeLabel="食事に関するその他の指導内容"
            freeValue={vals.food_free_text}
            onFreeChange={(v)=>set('food_free_text', v)}
          />
          <GuidanceGroup
            title="運動"
            name="athletic_guidance"
            values={vals.athletic_guidance}
            onChange={(arr)=>set('athletic_guidance', arr)}
            options={[
              '運動処方',
              '日常生活の活動量増加',
            ]}
            freeLabel="運動に関するその他の指導内容"
            freeValue={vals.athletic_free_text}
            onFreeChange={(v)=>set('athletic_free_text', v)}
          />
          <GuidanceGroup
            title="たばこ"
            name="tobacco_guidance"
            values={vals.tobacco_guidance}
            onChange={(arr)=>set('tobacco_guidance', arr)}
            options={[
              '非喫煙者である',
              '禁煙・節煙の有効性',
              '禁煙の実施方法等',
            ]}
            freeLabel="たばこに関するその他の指導内容"
            freeValue={vals.tobacco_free_text}
            onFreeChange={(v)=>set('tobacco_free_text', v)}
          />
          <GuidanceGroup
            title="その他"
            name="other_guidance"
            values={vals.other_guidance}
            onChange={(arr)=>set('other_guidance', arr)}
            options={[
              '仕事', '余暇', '睡眠の確保', '減量', '家庭での計測（歩数、体重、血圧、腹囲等）'
            ]}
            freeLabel="その他の指導内容"
            freeValue={vals.other_free_text}
            onFreeChange={(v)=>set('other_free_text', v)}
          />
        </>
      );
      case 'DOC_CERT':
        return (
          <>
            <Field label="氏名" v={vals.full_name} onV={(v)=>set('full_name', v)} />
            <Field label="生年月日" v={vals.birth_date} onV={(v)=>set('birth_date', v)} />
            <Field label="診断名" v={vals.diagnosis_name} onV={(v)=>set('diagnosis_name', v)} required={true}/>
            <TextArea label="診断書の中身詳細" v={vals.certificate_body_text} onV={(v)=>set('certificate_body_text', v)} required={true}/>
          </>
        );
      case 'DOC_INFO_ADDR':
        return (
          <>
            <Field label="氏名" v={vals.full_name} onV={(v)=>set('full_name', v)} required={true}/>
            <Field label="生年月日" v={vals.birth_date} onV={(v)=>set('birth_date', v)} required={true}/>
            <Field label="年齢" v={vals.age} onV={(v)=>set('age', v)} required={true}/>
            <Field label="性別" v={vals.gender} onV={(v)=>set('gender', v)} required={true}/>
            <Field label="傷病名" v={vals.disease_name} onV={(v)=>set('disease_name', v)} required={true}/>
            <Field label="宛名" v={vals.addressee} onV={(v)=>set('addressee', v)} placeholder="紹介先・医療機関名" />
            <TextArea label="紹介目的" v={vals.referral_purpose_text} onV={(v)=>set('referral_purpose_text', v)} required={true}/>
          </>
        );
      case 'DOC_DISABILITY':
        // 傷病手当金申請書
        return (
          <>
            <SectionTitle title="傷病手当金申請書" />
            <Field label="傷病名" v={vals.disease_name} onV={(v)=>set('disease_name', v)} required={true}/>
            <Field label="発病または負傷の原因" v={vals.cause_of_illness} onV={(v)=>set('cause_of_illness', v)} placeholder="不詳"/>
            <DateField label="発病または負傷の年月日" v={vals.onset_date} onV={(v)=>set('onset_date', v)} />
            <SelectField 
              label="前月との比較" 
              v={vals.monthly_comparison} 
              onV={(v)=>set('monthly_comparison', v)}
              options={['変化なし', '比較対象なし', '改善傾向', '増悪傾向', '動揺している', '改善傾向だが療養が必要']}
            />
            <CheckboxGroup
              label="治療内容"
              values={vals.treatment_contents || []}
              onChange={(arr)=>set('treatment_contents', arr)}
              options={['自宅療養', '精神療法', '薬物療法', '定期的な通院']}
            />
            <CheckboxGroup
              label="症状"
              values={vals.symptoms || []}
              onChange={(arr)=>set('symptoms', arr)}
              options={['抑うつ症状', '不安症状', '不眠症状', '食欲不振', '興味の減退', '意欲の減退', '集中困難', 'パニック発作']}
            />
            <TextArea label="補足" v={vals.supplementary_notes} onV={(v)=>set('supplementary_notes', v)} />
            <DateField label="労働不能と認めた開始日(分かる場合入力)" v={vals.work_inability_start_date} onV={(v)=>set('work_inability_start_date', v)} />
            <DateField label="労働不能と認めた終了日(分かる場合入力)" v={vals.work_inability_end_date} onV={(v)=>set('work_inability_end_date', v)} />
          </>
        );
      case 'DOC_WORK_CERT':
        // 就労可能証明書
        return (
          <>
            <SectionTitle title="就労可能証明書" />
            <Field label="傷病名" v={vals.disease_name} onV={(v)=>set('disease_name', v)} required={true}/>
            <TextArea label="現在の状態" v={vals.current_condition} onV={(v)=>set('current_condition', v)} required={true}/>
            <TextArea label="退職時の状態" v={vals.condition_at_retirement} onV={(v)=>set('condition_at_retirement', v)} required={true}/>
            <Field label="就労可能日" v={vals.work_possible_date} onV={(v)=>set('work_possible_date', v)} required={true}/>
            <TextArea label="健康保険の傷病手当、または労災保険の休業、補償申請の有無について" v={vals.insurance_benefit_status} onV={(v)=>set('insurance_benefit_status', v)} required={true}/>
          </>
        );
      case 'DOC_INFO_NOADDR':
      default:
        return (
          <>
            <Field label="氏名" v={vals.full_name} onV={(v)=>set('full_name', v)} required={true}/>
            <Field label="生年月日" v={vals.birth_date} onV={(v)=>set('birth_date', v)} required={true}/>
            <Field label="年齢" v={vals.age} onV={(v)=>set('age', v)} required={true}/>
            <Field label="性別" v={vals.gender} onV={(v)=>set('gender', v)} required={true}/>
            <Field label="傷病名" v={vals.disease_name} onV={(v)=>set('disease_name', v)} required={true}/>
            <TextArea label="紹介目的" v={vals.referral_purpose_text} onV={(v)=>set('referral_purpose_text', v)} required={true}/>
          </>
        );
    }
  })();

  // 保存時の必須チェック
  const handleSave = () => {
    const missing = [];
    for (const key of requiredFields) {
      const val = vals[key];
      if (val == null || String(val).trim() === '') {
        missing.push(key);
      }
    }
    if (missing.length > 0) {
      setAlertModal({ open: true, title: '入力エラー', message: '未入力の必須項目があります。入力してください。' });
      return;
    }
    onSave?.(vals);
  };

  const modal = (
    <div
      className="medichat-docmodal-overlay_v2"
      role="dialog"
      aria-modal="true"
      aria-label={`書類入力：${selectedDoc?.name || ''}`}
      onClick={(e)=>{ if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="medichat-docmodal-card_v2">
        <div className="medichat-docmodal-header_v2">
          <div className="medichat-docmodal-title_v2">
            <div className="medichat-docmodal-title-icon">📄</div>
            書類入力：{selectedDoc?.name || ''}
          </div>
          <button className="medichat-docmodal-close_v2" onClick={onClose}>×</button>
        </div>
        <div className="medichat-docmodal-body_v2">
          {body}
        </div>
        <div className="medichat-docmodal-footer_v2">
          <button className="medichat-docmodal-save_v2" onClick={handleSave}>保存</button>
        </div>
      </div>
      
      {/* AlertModal */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
    </div>
  );
  return ReactDOM.createPortal(modal, document.body);
}

function Field({ label, v, onV, placeholder, required = false }) {
  return (
    <div className="medichat-docmodal-field_v2">
      <label>
        {label} {required && <span style={{marginLeft:6, background:'#ef4444', color:'#fff', borderRadius:4, fontSize:11, padding:'2px 6px'}}>必須</span>}
      </label>
      <input value={v ?? ''} onChange={(e)=>onV(e.target.value)} placeholder={placeholder||''} required={required}/>
    </div>
  );
}
function TextArea({ label, v, onV, placeholder, required = false }) {
  return (
    <div className="medichat-docmodal-field_v2">
      <label>
        {label} {required && <span style={{marginLeft:6, background:'#ef4444', color:'#fff', borderRadius:4, fontSize:11, padding:'2px 6px'}}>必須</span>}
      </label>
      <textarea value={v ?? ''} onChange={(e)=>onV(e.target.value)} placeholder={placeholder||''} required={required}/>
    </div>
  );
}

function NumField({ label, v, onV, placeholder, required = false }) {
      return (
        <div className="medichat-docmodal-field_v2">
          <label>
            {label} {required && <span style={{marginLeft:6, background:'#ef4444', color:'#fff', borderRadius:4, fontSize:11, padding:'2px 6px'}}>必須</span>}
          </label>
          <input
            type="number"
            step="0.1"
            inputMode="decimal"
            value={v ?? ''}
            onChange={(e)=>onV(e.target.value)}
            placeholder={placeholder || ''}
            required={required}
          />
        </div>
      );
    }
    
    function SectionTitle({ title }) {
      return <div style={{fontWeight:600, margin:'8px 0 4px'}}>{title}</div>;
    }
    
    function Group({ two, children }) {
      return (
        <div style={{display:'grid', gap:8, gridTemplateColumns: two ? '1fr 1fr' : '1fr'}}>
          {children}
        </div>
      );
    }
    
    function GuidanceGroup({ title, name, values = [], onChange, options = [], freeLabel, freeValue, onFreeChange }) {
        const toggle = (val) => {
          const set = new Set(values);
          set.has(val) ? set.delete(val) : set.add(val);
          onChange?.(Array.from(set));
        };
        return (
          <div className="cp-modal-guidance-section medichat-guidance-block-v1" >
            <h4 className="cp-modal-guidance-title" style={{marginBottom:6}}>{title}</h4>
            <ul className="medichat-guidance-list-v1">
              {options.map((opt, i) => {
                const id = `doc-${name}-${i}`;
                const checked = values.includes(opt);
                return (
                  <li key={id}>
                    <input
                      id={id}
                      type="checkbox"
                      className="sr-only medichat-guidance-input-v1"
                      checked={checked}
                      onChange={() => toggle(opt)}
                    />
                    <label
                      htmlFor={id}
                      className={`medichat-guidance-option-v1 ${checked ? 'is-selected' : ''}`}
                    >
                      <span className="medichat-guidance-checkmark-v1" aria-hidden>
                        {checked ? '✓' : ''}
                      </span>
                      <span className="medichat-guidance-label-v1">{opt}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="cp-modal-free-input" style={{marginTop:6}}>
              <label className="cp-modal-form-label">{freeLabel}</label>
              <textarea
                className="cp-modal-form-textarea"
                rows={2}
                value={freeValue ?? ''}
                onChange={(e)=>onFreeChange?.(e.target.value)}
              />
            </div>
          </div>
        );
      }

    // 日付入力フィールド（iOS対応: value は YYYY-MM-DD 形式、保存時は YYYY/MM/DD に変換）
    function DateField({ label, v, onV, required = false }) {
      return (
        <div className="medichat-docmodal-field_v2">
          <label>
            {label} {required && <span style={{marginLeft:6, background:'#ef4444', color:'#fff', borderRadius:4, fontSize:11, padding:'2px 6px'}}>必須</span>}
          </label>
          <input
            type="date"
            value={toDateInputValue(v ?? '')}
            onChange={(e)=>onV(fromDateInputValue(e.target.value))}
            required={required}
            style={{width:'auto', minWidth:'150px'}}
          />
        </div>
      );
    }

    // セレクトボックス
    function SelectField({ label, v, onV, options = [], required = false }) {
      return (
        <div className="medichat-docmodal-field_v2">
          <label>
            {label} {required && <span style={{marginLeft:6, background:'#ef4444', color:'#fff', borderRadius:4, fontSize:11, padding:'2px 6px'}}>必須</span>}
          </label>
          <select value={v ?? ''} onChange={(e)=>onV(e.target.value)} required={required}>
            {options.map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    // チェックボックスグループ（複数選択可）
    function CheckboxGroup({ label, values = [], onChange, options = [] }) {
      const toggle = (val) => {
        const set = new Set(values);
        set.has(val) ? set.delete(val) : set.add(val);
        onChange?.(Array.from(set));
      };
      return (
        <div className="medichat-docmodal-field_v2">
          <label style={{marginBottom:8, display:'block'}}>{label}</label>
          <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
            {options.map((opt, i) => {
              const checked = values.includes(opt);
              return (
                <label key={i} style={{
                  display:'flex', 
                  alignItems:'center', 
                  gap:4, 
                  padding:'6px 12px',
                  border: checked ? '2px solid #3b82f6' : '1px solid #d1d5db',
                  borderRadius:6,
                  background: checked ? '#eff6ff' : '#fff',
                  cursor:'pointer',
                  fontSize:14
                }}>
                  <input 
                    type="checkbox" 
                    checked={checked} 
                    onChange={()=>toggle(opt)}
                    style={{width:16, height:16}}
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        </div>
      );
    }
